/**
 * iconMap.js
 * Maps icon key strings (stored in MongoDB) to React icon components.
 *
 * When course data is stored in MongoDB, JSX components like <FaLaptopCode />
 * cannot be stored directly. Instead we store a string key (e.g. "FaLaptopCode")
 * and resolve it here on the frontend.
 *
 * Usage in InternshipDetailPage:
 *   import { resolveIcon } from '../utils/iconMap';
 *   <span>{resolveIcon(feature.iconKey)}</span>
 */

import React from 'react';
import { FaLaptopCode, FaExchangeAlt, FaPython } from 'react-icons/fa';
import { BsFillPersonCheckFill } from 'react-icons/bs';
import { LuSparkles } from 'react-icons/lu';
import {
  FaAward, FaPersonChalkboard, FaGraduationCap, FaUserTie,
  FaChartColumn, FaSquarePollVertical, FaMicrosoft, FaRegFileExcel,
  FaCodeBranch, FaCloud, FaServer, FaDatabase,
} from 'react-icons/fa6';

/** Map of string key → React icon element */
const ICON_MAP = {
  // Font Awesome
  FaLaptopCode:          <FaLaptopCode />,
  FaExchangeAlt:         <FaExchangeAlt />,
  FaPython:              <FaPython />,

  // Bootstrap
  BsFillPersonCheckFill: <BsFillPersonCheckFill />,

  // Lucide
  LuSparkles:            <LuSparkles />,

  // Font Awesome 6
  FaAward:               <FaAward />,
  FaPersonChalkboard:    <FaPersonChalkboard />,
  FaGraduationCap:       <FaGraduationCap />,
  FaUserTie:             <FaUserTie />,
  FaChartColumn:         <FaChartColumn />,
  FaSquarePollVertical:  <FaSquarePollVertical />,
  FaMicrosoft:           <FaMicrosoft />,
  FaRegFileExcel:        <FaRegFileExcel />,
  FaCodeBranch:          <FaCodeBranch />,
  FaCloud:               <FaCloud />,
  FaServer:              <FaServer />,
  FaDatabase:            <FaDatabase />,
};

/**
 * Resolves an icon key or emoji string to a renderable React node.
 *
 * - If `key` matches a known icon name, returns the React element.
 * - If `key` is a non-empty string (e.g. emoji like "✅" or "🔹"), returns it as-is.
 * - If `key` is falsy, returns a generic fallback "•".
 *
 * @param {string} key - Icon identifier stored in MongoDB
 * @returns {React.ReactNode}
 */
export function resolveIcon(key) {
  if (!key) return '•';
  if (ICON_MAP[key]) return ICON_MAP[key];
  return key; // Treat as emoji / plain string
}

export default ICON_MAP;
